/**
 * The profile's media cards: per kind, its periods (POST/REEL/AUDIO) or its single refresh
 * interval (STORY), and whether comments and likers are read. Holds no data of its own.
 */

import { useId, type ReactNode } from 'react';

import { DurationInput, formatDuration } from '../../../../chrome/form';
import { Field, FieldLabel } from '../../../../ui/field';
import { Input } from '../../../../ui/input';
import { Switch } from '../../../../ui/switch';
import { KindCardFrame } from './KindCardFrame';
import { profileCatalogue } from './messages';
import { PeriodEditor } from './PeriodEditor';
import {
  MIN_AUDIO_FREQUENCY_SECONDS,
  MIN_FREQUENCY_SECONDS,
  totalSeconds,
  type PeriodDraft,
} from './periods';
import { PeriodSummary } from './PeriodSummary';

const t = profileCatalogue.t;

export type MediaKindName = 'POST' | 'REEL' | 'STORY';

export interface KindRow {
  media_kind: MediaKindName;
  enabled: 'on' | 'off' | 'auto';
  periods: PeriodDraft[];
  story_interval_seconds: number | null;
  comments_enabled: boolean;
  likers_enabled: boolean;
}

/** Audio is a kind of its own on the profile: the periods the track's reuse list is read
 *  through, counted from the source post's publication. */
export interface AudioRow {
  enabled: boolean;
  periods: PeriodDraft[];
  /** `null` means every reuse the lookup finds, to the end of the list. */
  page_limit: number | null;
}

function LabelledSwitch(props: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}): ReactNode {
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <Switch
        id={id}
        checked={props.checked}
        disabled={props.disabled}
        onCheckedChange={props.onChange}
      />
      <label htmlFor={id} className="text-sm">
        {props.label}
      </label>
    </div>
  );
}

function StoryInterval(props: {
  seconds: number | null;
  onChange: (seconds: number | null) => void;
}): ReactNode {
  const id = useId();
  return (
    <Field className="max-w-sm">
      <FieldLabel htmlFor={id}>{t('story.frequency')}</FieldLabel>
      <DurationInput
        id={id}
        seconds={props.seconds}
        minSeconds={MIN_FREQUENCY_SECONDS}
        units={['minutes', 'hours', 'days']}
        onChange={props.onChange}
      />
    </Field>
  );
}

/** An empty field means "to the end of the list" (`page_limit: null`); anything else must be a
 *  whole number of at least one, the api's own floor. */
function parsePageLimit(text: string): number | null {
  if (text.trim() === '') return null;
  const value = Number(text);
  return Number.isInteger(value) && value >= 1 ? value : null;
}

function AudioCard(props: {
  audio: AudioRow;
  onChange: (patch: Partial<AudioRow>) => void;
  onToggle: () => void;
}): ReactNode {
  const { audio } = props;
  const pageLimitId = useId();
  return (
    <KindCardFrame
      kind="AUDIO"
      enabled={audio.enabled}
      onToggle={props.onToggle}
      summary={formatDuration(totalSeconds(audio.periods), ['minutes', 'hours', 'days'])}
    >
      <div className="flex flex-col gap-4">
        <PeriodEditor
          periods={audio.periods}
          minFrequencySeconds={MIN_AUDIO_FREQUENCY_SECONDS}
          onChange={(periods) => props.onChange({ periods })}
        />
        <Field className="max-w-sm">
          <FieldLabel htmlFor={pageLimitId}>{t('audio.pageLimit.label')}</FieldLabel>
          <Input
            id={pageLimitId}
            type="number"
            min={1}
            step={1}
            placeholder={t('audio.pageLimit.toEnd')}
            value={audio.page_limit ?? ''}
            onChange={(event) => props.onChange({ page_limit: parsePageLimit(event.target.value) })}
          />
        </Field>
      </div>
    </KindCardFrame>
  );
}

export function KindCards(props: {
  rows: KindRow[];
  onChange: (kind: MediaKindName, patch: Partial<KindRow>) => void;
  onToggle: (kind: MediaKindName) => void;
  audio: AudioRow;
  onAudioChange: (patch: Partial<AudioRow>) => void;
  onAudioToggle: () => void;
}): ReactNode {
  return (
    <div className="flex flex-col gap-4">
      {props.rows.map((row) => {
        const kind = row.media_kind;
        const patch = (next: Partial<KindRow>) => props.onChange(kind, next);
        const story = kind === 'STORY';
        return (
          <KindCardFrame
            key={kind}
            kind={kind}
            enabled={row.enabled !== 'off'}
            onToggle={() => props.onToggle(kind)}
            summary={
              story ? (
                <PeriodSummary
                  frequencyOnly
                  period={{
                    frequency_seconds: row.story_interval_seconds,
                    duration_seconds: null,
                    color: null,
                  }}
                />
              ) : (
                formatDuration(totalSeconds(row.periods), ['minutes', 'hours', 'days'])
              )
            }
          >
            <div className="flex flex-col gap-4">
              {story ? (
                <StoryInterval
                  seconds={row.story_interval_seconds}
                  onChange={(seconds) => patch({ story_interval_seconds: seconds })}
                />
              ) : (
                <PeriodEditor periods={row.periods} onChange={(periods) => patch({ periods })} />
              )}
              {!story && (
                <>
                  <LabelledSwitch
                    label={t('collect.comments')}
                    checked={row.comments_enabled}
                    onChange={(checked) =>
                      patch({
                        comments_enabled: checked,
                        likers_enabled: checked && row.likers_enabled,
                      })
                    }
                  />
                  <LabelledSwitch
                    label={t('collect.likers')}
                    checked={row.likers_enabled}
                    disabled={!row.comments_enabled}
                    onChange={(checked) => patch({ likers_enabled: checked })}
                  />
                </>
              )}
            </div>
          </KindCardFrame>
        );
      })}
      <AudioCard
        audio={props.audio}
        onChange={props.onAudioChange}
        onToggle={props.onAudioToggle}
      />
    </div>
  );
}
